import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  ArrowDownTrayIcon,
  ClockIcon,
  ShieldCheckIcon,
  DocumentCheckIcon,
  ArrowsRightLeftIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

/* ─── Animation helpers (matches partner-page register) ─── */
const ease = [0.44, 0, 0.56, 1] as const;

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : {}}
      transition={{ duration: 0.7, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function ScaleIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.6, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── Data ─── */

const ingestToday = [
  {
    type: 'HMDA LAR submissions',
    format: 'CFPB-standard LAR format',
    time: '24 hours',
  },
  {
    type: 'Branch locations',
    format: 'Structured CSV',
    time: 'Under 5 minutes per bank',
  },
  {
    type: 'Assessment areas',
    format: 'Structured CSV (county or tract level)',
    time: 'Under 5 minutes per AA',
  },
  {
    type: 'Marketing content history',
    format: 'Paste or upload',
    time: 'Live on activation',
  },
  {
    type: 'Census tract demographics',
    format: 'Ingested from FFIEC source',
    time: 'Continuous',
  },
];

const ingestComing = [
  {
    type: 'Policy library',
    format: 'PDF or Word (bulk upload)',
    mechanism: 'AI metadata extraction with human review',
  },
  {
    type: 'Complaint history',
    format: 'Vendor CSV (Ncontracts, nCino, Wolters, Crowe, ComplyPilot)',
    mechanism: 'Auto-detected vendor mapping with atomic bulk commit',
  },
  {
    type: 'Exam findings & MRA tracking',
    format: 'Vendor CSV or XLSX',
    mechanism: 'Per-vendor mapping with prior-finding linkage preserved',
  },
];

const processSteps = [
  {
    num: 1,
    title: 'Request your data',
    description: "Send the export request to your incumbent vendor. Use the Data Export Rights brief below as the template — it lists exactly what to ask for and which contract clauses to invoke.",
  },
  {
    num: 2,
    title: 'Sign the mutual NDA',
    description: 'Standard mutual NDA covers the migration window. We can sign yours or provide ours, whichever your contracts team prefers.',
  },
  {
    num: 3,
    title: 'Hand off the export',
    description: 'Secure file transfer to a dedicated Clarid migration channel. Encrypted in transit and at rest. We never retain source files beyond what is loaded into your tenant.',
  },
  {
    num: 4,
    title: 'We configure your tenant',
    description: 'HMDA, branches, and assessment areas land within 48 hours. Policies, complaints, and exam findings within 5 business days. Weekly progress reports throughout.',
  },
  {
    num: 5,
    title: 'You verify and go live',
    description: 'Joint walkthrough with your compliance team. Spot-check the data, sign off, then your compliance program is running on Clarid — with full historical continuity for examiners.',
  },
];

const clauses = [
  {
    icon: ShieldCheckIcon,
    title: 'Data Ownership',
    description: 'Confirms your institution retains all rights to data submitted or generated within the platform. This clause is the foundation of every export request.',
  },
  {
    icon: ArrowsRightLeftIcon,
    title: 'Data Portability',
    description: "Vendor obligation to provide your data in machine-readable format on request. Specify CSV or your vendor's native export — both are acceptable.",
  },
  {
    icon: ClockIcon,
    title: 'Termination Assistance',
    description: 'Vendor obligation to support your transition for a defined period — typically 60 to 90 days — at no incremental charge or at pre-agreed rates.',
  },
  {
    icon: DocumentCheckIcon,
    title: 'Audit Rights',
    description: 'Your right to review the controls and access records relevant to your data. Useful when you need a complete audit trail through the transition window.',
  },
];

/* ─── Component ─── */
export default function MigratePage() {
  useEffect(() => {
    document.title = 'Migrating to Clarid — Move your compliance data without the friction';

    const metaTags: Record<string, string> = {
      'description': 'How banks migrate to Clarid: the data export rights, the 48-hour ingest window for HMDA and AAs, the 5-business-day promise for policies and complaints. A one-page brief for circulation to legal, IT, and vendor management.',
      'og:title': 'Migrating to Clarid · Your compliance data is yours',
      'og:description': "Switching compliance vendors is friction — but the data is yours. Clarid receives your historical record on a defined timeline, with the audit trail your examiners expect.",
      'og:type': 'website',
      'og:image': '/clarid-logo.png',
      'twitter:card': 'summary_large_image',
      'twitter:title': 'Migrating to Clarid',
      'twitter:description': 'Your compliance data is yours. Moving it is our job.',
    };

    const existingTags: HTMLMetaElement[] = [];
    for (const [key, content] of Object.entries(metaTags)) {
      const meta = document.createElement('meta');
      if (key.startsWith('og:') || key.startsWith('twitter:')) {
        meta.setAttribute('property', key);
      } else {
        meta.setAttribute('name', key);
      }
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
      existingTags.push(meta);
    }

    return () => {
      existingTags.forEach((tag) => document.head.removeChild(tag));
      document.title = 'Clarid — Clarity in every communication';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-body text-slate-600">
      {/* ─── Minimal Header ─── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 py-5 flex items-center">
          <img src="/clarid-logo.png" alt="Clarid" className="h-10" />
        </div>
      </header>

      {/* ─── Hero ─── */}
      <section className="pt-36 pb-24 md:pt-48 md:pb-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeUp>
            <p className="text-base font-semibold tracking-[0.2em] uppercase text-indigo-600 mb-8">
              Migration
            </p>
          </FadeUp>

          <FadeUp delay={0.1}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] font-display font-bold text-navy-900 leading-[1.1] mb-10 max-w-5xl mx-auto">
              Your compliance data is yours.<br />Moving it is our job.
            </h1>
          </FadeUp>

          <FadeUp delay={0.2}>
            <p className="text-xl md:text-2xl lg:text-[1.65rem] text-slate-500 max-w-4xl mx-auto leading-relaxed">
              The friction of switching compliance vendors is rarely the data itself. It's the
              assumption — usually wrong — that the current vendor will resist handing it back.
              Clarid receives your historical record cleanly, on a defined timeline, with the audit
              trail your examiners expect.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 1: The principle ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
                The principle: your data is yours
              </h2>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="space-y-7">
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Under standard vendor contracts, the compliance data your institution submits or
                  generates — your HMDA records, your complaint files, your policy library, your
                  fair lending analyses, your exam findings — belongs to your institution. Not to
                  your vendor.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Most master service agreements already include the clauses that let you request
                  it back: Data Ownership, Data Portability, Termination Assistance, Audit Rights.
                  The friction usually comes from not knowing what to ask for — not from a vendor
                  refusing.
                </p>
                <p className="text-xl md:text-[1.35rem] font-semibold text-navy-900 mt-10">
                  We've written a one-page brief that lists exactly what to request, which clauses
                  to cite, and what format to ask for. Designed to be circulated as-is to your
                  legal, IT, and vendor management teams.
                </p>
              </div>
            </FadeUp>

            <FadeUp delay={0.3}>
              <div className="mt-12 flex justify-center">
                <a
                  href="/Clarid-Data-Export-Rights-Brief.pdf"
                  download
                  className="inline-flex items-center gap-3 px-8 py-4 bg-navy-900 text-white text-lg font-medium rounded-lg hover:bg-navy-800 transition-colors duration-300 shadow-sm hover:shadow-md"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  Download the Data Export Rights brief
                </a>
              </div>
            </FadeUp>

            <FadeUp delay={0.4}>
              <p className="mt-6 text-center text-sm text-slate-400">
                One page. No email required. Reference: CLARID-MIG-001.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 2: What we ingest today ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <p className="text-base font-semibold tracking-[0.2em] uppercase text-indigo-600 text-center mb-5">
              Live today
            </p>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
              What lands in your tenant immediately
            </h2>
          </FadeUp>

          <div className="max-w-5xl mx-auto">
            <FadeUp delay={0.1}>
              <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-sm">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold tracking-wider uppercase text-slate-500">
                        Data type
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold tracking-wider uppercase text-slate-500">
                        Source format
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold tracking-wider uppercase text-slate-500">
                        Time to live
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {ingestToday.map((row) => (
                      <tr key={row.type} className="hover:bg-slate-50/50 transition-colors duration-200">
                        <td className="px-6 py-5 text-lg font-semibold text-navy-900">{row.type}</td>
                        <td className="px-6 py-5 text-lg text-slate-600">{row.format}</td>
                        <td className="px-6 py-5 text-lg font-medium text-indigo-700">{row.time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 3: What we're adding ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <p className="text-base font-semibold tracking-[0.2em] uppercase text-indigo-600 text-center mb-5">
              Coming Q3 2026
            </p>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
              What we're automating next
            </h2>
          </FadeUp>

          <div className="max-w-5xl mx-auto">
            <FadeUp delay={0.1}>
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold tracking-wider uppercase text-slate-500">
                        Data type
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold tracking-wider uppercase text-slate-500">
                        Source format
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold tracking-wider uppercase text-slate-500">
                        Mechanism
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {ingestComing.map((row) => (
                      <tr key={row.type} className="hover:bg-slate-50/50 transition-colors duration-200">
                        <td className="px-6 py-5 text-lg font-semibold text-navy-900">{row.type}</td>
                        <td className="px-6 py-5 text-lg text-slate-600">{row.format}</td>
                        <td className="px-6 py-5 text-lg text-slate-600">{row.mechanism}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </FadeUp>

            <FadeUp delay={0.3}>
              <p className="mt-10 text-center text-lg text-slate-500 max-w-3xl mx-auto leading-relaxed">
                While these importers are being built, Clarid handles every migration white-glove.
                The 5-business-day timeline below holds regardless of which mechanism we use under
                the hood.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 4: The promise ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-16">
              The migration promise
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 max-w-5xl mx-auto">
            <ScaleIn delay={0.1}>
              <div className="p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500 h-full">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-indigo-50 flex items-center justify-center">
                    <ClockIcon className="h-7 w-7 text-indigo-600" />
                  </div>
                  <span className="text-base font-semibold tracking-[0.18em] uppercase text-indigo-600">
                    48 hours
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-display font-bold text-navy-900 mb-4">
                  HMDA, branches, assessment areas
                </h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  Direct ingest. HMDA LARs through our LAR uploader (CFPB-standard, no translation
                  needed). Branches and assessment areas through structured CSV templates. Live in
                  your tenant within two business days of receiving the export.
                </p>
              </div>
            </ScaleIn>

            <ScaleIn delay={0.2}>
              <div className="p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500 h-full">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-indigo-50 flex items-center justify-center">
                    <DocumentCheckIcon className="h-7 w-7 text-indigo-600" />
                  </div>
                  <span className="text-base font-semibold tracking-[0.18em] uppercase text-indigo-600">
                    5 business days
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-display font-bold text-navy-900 mb-4">
                  Policies, complaints, exam history
                </h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  White-glove during the rollout of the bulk importers; fully self-serve once those
                  ship. Either way, the timeline holds: a complete export from your prior vendor
                  becomes a live Clarid tenant within five business days.
                </p>
              </div>
            </ScaleIn>
          </div>
        </div>
      </section>

      {/* ─── Section 5: How it works ─── */}
      <section className="py-24 md:py-36 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '48px 48px',
        }} />

        <div className="relative max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white text-center mb-16">
              How it works
            </h2>
          </FadeUp>

          <div className="max-w-4xl mx-auto space-y-10 lg:space-y-12">
            {processSteps.map((step, i) => (
              <FadeUp key={step.num} delay={i * 0.08}>
                <div className="flex items-start gap-7">
                  <div className="flex-shrink-0 w-14 h-14 rounded-full bg-white text-navy-900 flex items-center justify-center text-lg font-bold">
                    {step.num}
                  </div>
                  <div className="pt-2">
                    <h3 className="text-xl md:text-2xl font-display font-bold text-white mb-2">
                      {step.title}
                    </h3>
                    <p className="text-lg text-slate-400 leading-relaxed">{step.description}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 6: Contract clauses ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 mb-8">
                Contract clauses to invoke
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 max-w-4xl mx-auto leading-relaxed">
                Most vendor master agreements include one or more of these clauses. Cite the
                specific clause in your written export request and set a delivery timeline of
                30 calendar days.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 max-w-5xl mx-auto">
            {clauses.map((clause, i) => (
              <ScaleIn key={clause.title} delay={i * 0.08}>
                <div className="group p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:border-indigo-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 group-hover:scale-110 transition-all duration-300">
                      <clause.icon className="h-6 w-6 text-indigo-600" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{clause.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{clause.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>

          <FadeUp delay={0.4}>
            <p className="mt-12 text-center text-base text-slate-400 max-w-3xl mx-auto leading-relaxed italic">
              This page is provided as an educational resource and does not constitute legal advice.
              Confirm specific contract terms with your institution's counsel before acting.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 7: Why this matters / closing ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 mb-10">
                Switching isn't the problem.<br />Knowing what to ask is.
              </h2>
            </FadeUp>

            <FadeUp delay={0.1}>
              <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed mb-6">
                Banks stay on legacy compliance platforms longer than they should because the
                imagined cost of moving exceeds the imagined value of switching. The Data Export
                Rights brief removes the first half of that equation. The 48-hour and
                5-business-day promises remove the second.
              </p>
              <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                Together, they let you treat a vendor change as a project with a defined timeline —
                not a multi-quarter ordeal.
              </p>
            </FadeUp>

            <FadeUp delay={0.3}>
              <div className="mt-14 flex flex-col sm:flex-row justify-center gap-4">
                <a
                  href="/Clarid-Data-Export-Rights-Brief.pdf"
                  download
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-navy-900 text-white text-lg font-medium rounded-lg hover:bg-navy-800 transition-colors duration-300"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  Download the brief
                </a>
                <a
                  href="mailto:bobby@clarid.ai?subject=Migration%20conversation"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-white text-navy-900 text-lg font-medium rounded-lg border border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all duration-300"
                >
                  <EnvelopeIcon className="h-5 w-5" />
                  Talk to us
                </a>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Contact ─── */}
      <section className="py-24 md:py-32">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeIn>
            <div className="space-y-3">
              <p className="text-3xl font-display font-bold text-navy-900">Bobby Sloan</p>
              <p className="text-lg text-slate-500">Founder & CEO, Clarid</p>
              <div className="flex items-center justify-center gap-3 mt-4 text-lg text-indigo-600">
                <EnvelopeIcon className="h-5 w-5" />
                <a href="mailto:bobby@clarid.ai" className="hover:text-indigo-700 transition-colors">
                  bobby@clarid.ai
                </a>
              </div>
              <p className="text-base text-slate-400 mt-1">clarid.ai</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── Minimal footer ─── */}
      <footer className="py-8 border-t border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src="/clarid-logo.png" alt="Clarid" className="h-7 opacity-40" />
          <p className="text-slate-400 text-sm">
            &copy; {new Date().getFullYear()} Avancee LLC d/b/a Clarid. Reference: CLARID-MIG-005.
          </p>
        </div>
      </footer>
    </div>
  );
}
